import { Container, ExternalLink } from 'lucide-react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { cn } from '@reflex/ui/lib/utils';
import { ResourcePreviewCard } from '@reflex/ui/components/resource-preview/ResourcePreviewCard';
import { SANDBOX_STATUS_CONFIG, deriveSandboxState } from '@/lib/status-colors';
import { usePlatformUrl } from '@/hooks/config/useConfig';
import { getRunloopDevboxUrl } from '@/lib/runloop-urls';
import { ArchBadge } from '@reflex/ui/components/shared/ArchBadge';
import { DevboxSizeBadge } from './DevboxSizeBadge';
import type { AgentStatus, BlueprintArchitecture, ResourceSize } from '@/lib/types';

const KIND_LABEL = 'Sandbox';

interface SandboxPreviewProps {
  devboxId: string | null;
  agentStatus: AgentStatus;
  /**
   * Live Runloop devbox status (raw string, e.g. `running` / `suspended` /
   * `resuming`) from the `/agents/:id/devbox-status` poll. When supplied and
   * decisive it takes precedence over the agent-status-derived state. Omit
   * (or pass `null` / `'unknown'`) to fall back to that derivation.
   */
  devboxStatus?: string | null;
  /**
   * Devbox resource size (`agent.sandboxOptions.resourceSize`). When present a
   * {@link DevboxSizeBadge} is shown beside the state badge. Omit when the size
   * is unknown (e.g. legacy launches that never recorded one).
   */
  resourceSize?: ResourceSize | null;
  /**
   * CPU architecture the devbox is running at, from the
   * `/agents/:id/devbox-status` poll. When present an {@link ArchBadge}
   * is shown beside the size badge. Omit when unknown (poll off or not yet
   * resolved, snapshot launch, or a box with no blueprint to derive it from).
   */
  architecture?: BlueprintArchitecture | null;
}

/**
 * Hover/click preview for an agent's sandbox (Runloop devbox). Renders the
 * shared {@link ResourcePreviewCard} shell with the same `Container` icon the
 * status badge uses, the derived sandbox state, the devbox id, and an external
 * link to the Runloop dashboard. Mirrors the user/org owner-chip previews so
 * the sandbox indicator reads consistently across the app.
 */
export function SandboxPreview({
  devboxId,
  agentStatus,
  devboxStatus,
  resourceSize,
  architecture,
}: SandboxPreviewProps) {
  const platformUrl = usePlatformUrl();
  const devboxUrl = getRunloopDevboxUrl(devboxId, platformUrl);
  const state = deriveSandboxState(devboxId, agentStatus, devboxStatus);
  const config = SANDBOX_STATUS_CONFIG[state];

  const meta = (
    <>
      <Badge
        variant="outline"
        className={cn('text-[10px] py-0 px-1.5 gap-1', config.badge)}
        data-testid="sandbox-preview-state"
      >
        <span className={cn('h-1.5 w-1.5 rounded-full', config.dot)} />
        {config.label}
      </Badge>
      {resourceSize ? <DevboxSizeBadge size={resourceSize} /> : null}
      {architecture ? <ArchBadge architecture={architecture} /> : null}
    </>
  );

  const description = devboxUrl ? (
    <a
      href={devboxUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-foreground/80 transition-colors hover:text-foreground"
      aria-label="Open devbox in Runloop"
    >
      <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
      Open in Runloop
    </a>
  ) : devboxId ? null : (
    <p>No devbox has been provisioned for this agent yet.</p>
  );

  // The full devbox id only appears once — in the card's canonical id row.
  // The name carries the human-readable sandbox state instead.
  return (
    <ResourcePreviewCard
      kindLabel={KIND_LABEL}
      icon={Container}
      name={config.label}
      meta={meta}
      description={description}
      id={devboxId ?? '—'}
    />
  );
}
